import type { IEmployeeTalkState } from '@sharedInterfaces/IEmployeeTalkState';
import type { EmployeeTalkEventDispatcher } from '@src/App/Services/EmployeeTalkEventDispatcher';
import { useCallback, useRef } from 'react';

interface NavigationDeps {
    talkState: IEmployeeTalkState | null;
    dispatcherRef: React.MutableRefObject<EmployeeTalkEventDispatcher | null>;
    isDispatcherReady: () => boolean;
    setError: (msg: string | null) => void;
}

export const useEmployeeTalkNavigation = ({
    talkState,
    dispatcherRef,
    isDispatcherReady,
    setError,
}: NavigationDeps) => {
    const currentStep = talkState?.navigation.currentStep ?? 0;

    const canNavigateToStep = useCallback(
        (step: number): boolean => {
            return talkState?.navigation.availableSteps.includes(step) || false;
        },
        [talkState],
    );

    const lastNavigationRef = useRef<number>(0);
    const NAVIGATION_DEBOUNCE_MS = 500;

    const navigateToStep = useCallback(
        async (step: number): Promise<void> => {
            if (!isDispatcherReady()) return;

            const now = Date.now();
            if (now - lastNavigationRef.current < NAVIGATION_DEBOUNCE_MS) return;
            lastNavigationRef.current = now;

            const dispatcher = dispatcherRef.current;
            if (!dispatcher) return;

            try {
                await dispatcher.navigateToStep(step);
            } catch (err) {
                console.error('❌ Navigation Error:', err);
                setError(err instanceof Error ? err.message : 'Unknown error');
            }
        },
        [dispatcherRef, isDispatcherReady, setError],
    );

    return {
        currentStep,
        canNavigateToStep,
        navigateToStep,
    };
};
